import {
  FormControl,
  TextField,
  Button,
  CircularProgress,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListSubheader,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import { workflowGroupsToRcTree } from "../../../utils/rctreeDataConversion";
import Tree from "rc-tree";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { treeActions } from "../../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { newTemplateActions } from "../../../redux/slices/newTemplateSlice";
import inputValidation from "../../../utils/inputValidation";
import { styled } from "@mui/material/styles";
import Split from "react-split";
import CloseIcon from "@mui/icons-material/Close";

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: "100%",
    backgroundColor: "transparent",
    boxShadow: "none",
  },
}));

function NewWorkflowWizard({ modalOpen, toggle }) {
  const { t } = useTranslation();
  const [wfType, setWfType] = useState("new");
  const [wfName, setWfName] = useState("");
  const [valid, setValid] = useState(true);
  const [disableFinish, setDisableFinish] = useState(true);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState("");
  const [accountOptions, setAccountOptions] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedNode, setSelectedNode] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const selectedAccount = useSelector((state) => state.queryParams.account);
  const currentScreen = useSelector((state) => state.treeData.currentScreen);
  const leftTreeSelectedNode = useSelector(
    (state) =>
      state.treeData[state.treeData.currentScreen].leftTree.tree.body
        .selectedNode
  );
  const accountErrorRef = useRef(null);
  const errorRef = useRef(null);
  const error = useRef(null);
  const nodeIcon = {
    workflow: "CALL_WORKFLOW",
    workflowGroup: "RECORD",
  };

  const accountDropDownItems = accountOptions?.map((obj, key) => (
    <MenuItem key={key} value={obj}>
      {obj}
    </MenuItem>
  ));
  const latestAccountRef = useRef("");

  const validateInput = (inputFieldValue) => {
    if (inputValidation(inputFieldValue) === "") {
      error.current.textContent = t("BOX_ENTER_NEW_NM_FOR_NEW_WF_GROUP");
      setValid(false);
      setDisableFinish(true);
      return false;
    } else if (inputValidation(inputFieldValue) === "invalid") {
      error.current.textContent = t(
        "BOX_WF_NAMES_CANNOT_CONTAIN_CERTAIN_CHARS_OR_SPACES"
      );
      setValid(false);
      setDisableFinish(true);
      return false;
    } else if (inputFieldValue.length > 50) {
      const errorMsg = t("BOX_MAX_LENGTH_FOR_NAME_IS").replace(/\{0\}/, "50");
      error.current.textContent = errorMsg;
      setValid(false);
      setDisableFinish(true);
      return false;
    }
    return true;
  };

  const handleWfNameChange = (e) => {
    const { value } = e.target;
    setValid(true);
    error.current.textContent = "";
    setWfName(value);
    const trimmedValue = value.trim();
    if (
      trimmedValue.length > 0 &&
      (wfType === "new" || (wfType === "copy" && selectedNode.key))
    ) {
      setDisableFinish(false);
    } else {
      setDisableFinish(true);
    }
  };

  const handleAccountChange = (e) => {
    const { value } = e.target;
    setAccount(value);
    latestAccountRef.current = value;
    getTreeData(value);
  };

  const handleFinishClick = async () => {
    if (wfName && validateInput(wfName)) {
      setLoading(true);
      try {
        const wfgName =
          leftTreeSelectedNode.type === "workflowGroup"
            ? leftTreeSelectedNode.title
            : leftTreeSelectedNode.workflowGroup;
        const response = await dispatch(
          api.endpoints.addWorkflowTemplate.initiate({
            account: selectedAccount,
            workflow_group_name: encodeURIComponent(wfgName),
            workflow_name: encodeURIComponent(wfName.toLowerCase()),
            type: wfType === "new" ? "NEW" : "COPY",
            source_workflow_name: encodeURIComponent(selectedNode.title),
            source_workflow_group_name: encodeURIComponent(
              selectedNode.workflowGroup
            ),
            source_account: account,
          }, {forceRefetch: true})
        );
        if (response?.data?.current_state === "SUCCESS") {
          dispatch(
            treeActions.setLeftTreeContextActionValue({
              screen: parentScreen,
              data: {
                name: response?.data?.data.workflow_name,
                workflowGroup: wfgName,
              },
            })
          );

          dispatch(
            api.endpoints.getWorkflows.initiate(
              {
                account: selectedAccount,
              },
              { forceRefetch: true }
            )
          );

          dispatch(
            api.endpoints.getWorkflow.initiate({
              account: selectedAccount,
              workflow_group_name: wfgName,
              workflow_name: wfName.toLowerCase(),
            })
          );
          dispatch(newTemplateActions.setShowAlert(true));
          dispatch(newTemplateActions.setAlertType("success"));
          dispatch(
            newTemplateActions.setAlertMessage(
              response?.data?.message || "Workflow created successfully"
            )
          );
          toggle();
        } else {
          dispatch(newTemplateActions.setShowAlert(true));
          dispatch(newTemplateActions.setAlertType("error"));
          dispatch(
            newTemplateActions.setAlertMessage(
              response?.data?.message || t("BOX_MSG_GENERAL_ERROR")
            )
          );
        }
      } catch (error) {
        dispatch(newTemplateActions.setShowAlert(true));
        dispatch(newTemplateActions.setAlertType("error"));
        dispatch(
          newTemplateActions.setAlertMessage(t("BOX_MSG_GENERAL_ERROR"))
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelClick = () => {
    setValid(true);
    toggle();
  };
  const handleListItemClick = (e, index, value) => {
    setSelectedIndex(index);
    setWfType(value);
  };
  const Icon = (node) => {
    return <span className={nodeIcon[node.type]}></span>;
  };

  const switcherIcon = (obj) => {
    if (obj.isLeaf) return;
    if (obj.expanded) return <ArrowDropDownIcon />;
    else return <ArrowRightIcon sx={{ color: "#666666" }} />;
  };

  const onExpand = (keys) => {
    setExpandedKeys(keys);
  };

  const onSelect = (keys, info) => {
    if (info.node.isLeaf) {
      setSelectedKeys([info.node.key]);
      setSelectedNode(info.node);
      if (wfName.trim().length) setDisableFinish(false);
    } else {
      setErrorMessage(info.node.title);
      setErrorModal(true);
      setSelectedKeys([]);
      setSelectedNode("");
    }
  };

  const errorMsg = (
    <span>
      {t("BOX_SEL_FILE_TO_COPY_")} <b>{errorMessage}</b> {t("_FRG_IS_A_DIR")}
    </span>
  );
  const handleSelectedNode = useCallback((response, name, workflowGroup, account) => {
    if (
      response &&
      response.data &&
      response.data.data.workflow_groups &&
      workflowGroup
    ) {
      let selectedKey;
      let workflowKey;
      let node;
      const workflowGroups = workflowGroupsToRcTree(
        response.data.data,
        currentScreen
      );
      for (let i = 0; i < workflowGroups.length; i++) {
        const workflow = workflowGroups[i];

        // If workflow group matches
        if (workflow.title === workflowGroup) {
          // Case when there are no children
          if (workflow.children.length === 0) {
            selectedKey = workflow.key;
            workflowKey = workflow.key;
          }
          // Case when there are children
          else {
            const templates = workflow.children;
            for (let j = 0; j < templates.length; j++) {
              const template = templates[j];
              if (template.title === name) {
                node = template;
                node.sourceAccount = account;
                selectedKey = template.key;
                workflowKey = workflow.key;
                dispatch(
                  newTemplateActions.setCopyTemplateTreeSelectedNode(node)
                );
                break;
              } else {
                selectedKey = workflow.key;
                workflowKey = workflow.key;
              }
            }
          }
        }

        if (selectedKey) break;
      }

      if (selectedKey && workflowKey) {
        setSelectedKeys([selectedKey]);
        setExpandedKeys([workflowKey]);
      }
    }
  }, [currentScreen, dispatch]);
  // display available workflows in tree format
  const getTreeData = useCallback(async (account) => {
    if (account) {
      latestAccountRef.current = account;
      setTreeLoading(true);
      try {
        const response = await dispatch(
          api.endpoints.getWorkflowsOriginal.initiate(
            {
              account,
            },
            { forceRefetch: true }
          )
        );
        if (response?.data?.current_state === "SUCCESS") {
          if (account !== latestAccountRef.current) return;
          setTreeData(workflowGroupsToRcTree(response.data.data, "workflow"));
          const template =
            leftTreeSelectedNode.type !== "workflowGroup"
              ? leftTreeSelectedNode.title
              : "";
          const workflowGroup =
            leftTreeSelectedNode.type === "workflowGroup"
              ? leftTreeSelectedNode.title
              : leftTreeSelectedNode.workflowGroup;
          handleSelectedNode(response, template, workflowGroup, account);
        }
      } catch (error) {
        errorRef.current.textContent =
          error || error.message || t("BOX_MSG_GENERAL_ERROR");
      } finally {
        setTreeLoading(false);
      }
    }
  }, [dispatch, handleSelectedNode, leftTreeSelectedNode, t]);
  // Get the list of accounts and set the default account and its tree data
  useEffect(() => {
    if (wfType === "copy") {
      setDisableFinish(true);
      accountErrorRef.current.textContent = "";
      setAccountsLoading(true);
      dispatch(api.endpoints.getAccounts.initiate({ forceRefetch: true })).then(
        (response) => {
          if (response.error) {
            accountErrorRef.current.textContent =
              response.error.message || response.message || t("ACC_NOT_FOUND");
          }
          if (response && response.data.current_state === "SUCCESS") {
            let accounts = response?.data?.data?.accounts;
            setAccountOptions(accounts);
            if (accounts?.length !== 0) {
              const displayAccount = accounts.includes(selectedAccount)
                ? selectedAccount
                : accounts[0];
              setAccount(displayAccount);
              latestAccountRef.current = displayAccount;
              getTreeData(displayAccount);
            } else setAccount("");
          }
        }
      ).finally(() => setAccountsLoading(false));
    }
  }, [dispatch, getTreeData, selectedAccount, wfType]);

  return (
    <>
      <StyledDrawer anchor="left" open={modalOpen} onClose={toggle}>
        <Split
          className="split height-100 flex"
          direction="horizontal"
          gutterSize={4}
          snapOffset={0}
          dragInterval={4}
          minSize={[550, 0]}
          sizes={[67, 33]}>
          <div
            className="flex-column height-100 width-100"
            style={{ backgroundColor: "#fff" }}>
            <div
              style={{
                borderBottom: "1px solid #e0e0e0",
                height: "40px",
                boxSizing: "border-box",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: "16px",
              }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "18px",
                }}>
                <span
                  className="WIZARD padding-right-8"
                  style={{
                    backgroundSize: "18px",
                    width: "18px",
                    height: "18px",
                  }}></span>
                {t("TIT_NEW_WF_WIZ")}
              </div>
              <IconButton onClick={toggle} sx={{ borderRadius: "5px" }}>
                <CloseIcon
                  sx={{ height: "28px", width: "28px", color: "#777777" }}
                />
              </IconButton>
            </div>
            <Split
              style={{ height: "calc(100% - 42px)" }}
              className="split width-100 flex"
              direction="horizontal"
              gutterSize={4}
              snapOffset={0}
              dragInterval={4}
              minSize={[200, 275]}
              sizes={[30, 70]}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "20%",
                  padding: "5px",
                  height: "100%",
                  boxSizing: "border-box",
                }}>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "5px",
                    height: "100%",
                  }}>
                  <List
                    subheader={
                      <ListSubheader
                        component="div"
                        sx={{
                          backgroundColor: "transparent",
                          fontSize: "1rem",
                          color: "#333",
                          lineHeight: "normal",
                          padding: "4px 4px 8px ",
                          marginLeft: "4px",
                          marginTop: "6px",
                          borderBottom: "solid 1px #e0e0e0",
                        }}>
                        {t("DIAG_WORKFLOW_WIZARD1_DESC")}
                      </ListSubheader>
                    }>
                    {[
                      { text: t("BTN_CREATE_NEW_WF"), value: "new" },
                      { text: t("BTN_COPY_EXIST_WF"), value: "copy" },
                    ].map((item, index) => (
                      <ListItem
                        key={item.text}
                        value={item.value}
                        disablePadding>
                        <ListItemButton
                          selected={selectedIndex === index}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#CBF0FF",
                            },
                            height: "25px",
                            margin: "6px",
                          }}
                          onClick={(e) =>
                            handleListItemClick(e, index, item.value)
                          }>
                          <span className="CALL_WORKFLOW"></span>
                          <ListItemText
                            style={{ margin: "5px", fontWeight: 300 }}
                            primary={item.text}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </div>
              <div style={{ padding: "5px", width: "100%" }}>
                <div
                  style={{
                    padding: "5px",
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "5px",
                  }}>
                  <div className="ntw-content-header">
                    <span className="CALL_WORKFLOW padding-right-8"></span>
                    <span>
                      {wfType === "new"
                        ? t("BTN_CREATE_NEW_WF")
                        : t("BTN_COPY_EXIST_WF")}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                      padding: "5px",
                      boxSizing: "border-box",
                    }}>
                    {wfType === "copy" && (
                      <div>
                        <Stack spacing={"8px"}>
                          <FormControl
                            variant="outlined"
                            className="width-100"
                            size="small"
                            style={{ marginTop: "10px" }}>
                            <InputLabel id="accounts">
                              {t("ACCOUNT_")}
                            </InputLabel>
                            <Select
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                              className="width-100"
                              name="account"
                              label={t("ACCOUNT_")}
                              value={account}
                            disabled={
                              accountsLoading ||
                              treeLoading ||
                              !accountOptions.length
                            }
                              onChange={handleAccountChange}>
                              {accountDropDownItems}
                            </Select>
                          </FormControl>
                          <p ref={accountErrorRef} className="error"></p>
                          {treeLoading ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "300px",
                                maxHeight: "300px",
                              }}>
                              <CircularProgress size={"20px"} />
                            </div>
                          ) : treeData.length ? (
                            <div className="ntw-scrollable-container">
                              <Tree
                                className="mycls"
                                treeData={treeData}
                                showIcon
                                icon={Icon}
                                switcherIcon={switcherIcon}
                                selectedKeys={selectedKeys}
                                expandedKeys={expandedKeys}
                                onExpand={onExpand}
                                onSelect={onSelect}
                                showLine
                              />
                            </div>
                          ) : (
                            <div className="panel-no-data">
                              {t("NO_WFG_FOUND_FOR_ACC")}
                            </div>
                          )}
                          <p className="error" ref={errorRef}></p>
                        </Stack>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        marginTop: "20px",
                      }}>
                      <div style={{ flexGrow: 1 }}>
                        <TextField
                          variant="outlined"
                          size="small"
                          className="width-100"
                          autoFocus={true}
                          autoComplete="off"
                          value={wfName}
                          label={t("WORKFLOW_NAME")}
                          error={!valid}
                          onChange={handleWfNameChange}
                        />
                      </div>
                      <p
                        className="error"
                        style={{ marginTop: "10px" }}
                        ref={error}></p>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={disableFinish}
                        onClick={handleFinishClick}>
                        {loading ? (
                          <CircularProgress size={"16px"} />
                        ) : (
                          t("FINISH")
                        )}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCancelClick}>
                        {t("CANCEL")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Split>
            {errorModal && (
              <ErrorModal
                errorModal={errorModal}
                errorMessage={errorMsg}
                handleErrorModal={() => setErrorModal(false)}
              />
            )}
          </div>
          <div style={{display:'none'}} />
        </Split>
      </StyledDrawer>
    </>
  );
}

export default NewWorkflowWizard;
